import React from "react";

import {
  AddTask,
  ArrowBack,
  DeleteOutline,
  EditOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { Autosave } from "@/components/alerts/add-v2/persistence/useAutosave";

import { SaveIndicator } from "./SaveIndicator";

/**
 * What the officer can do, which is decided by one thing: whether the bulletin is already live.
 *
 * A **draft** has somewhere to go and something to throw away. A **published bulletin** has
 * neither — every edit is live the moment autosave sends it, so there is nothing to finalize, and
 * deleting a bulletin the public has seen is not an edit-screen action. All that is left is
 * leaving.
 */
export type FormActions =
  | {
      kind: "draft";
      onFinalize: () => void;
      onDiscard: () => void;
      /** Disables both while one of them is in flight. */
      busy: boolean;
    }
  | { kind: "published"; onBack: () => void };

interface FormActionBarProps {
  /** 0 means every required field is satisfied. */
  requiredLeft: number;
  /** Opens the topmost offending section and scrolls to it. */
  onJumpToFirstIssue: () => void;
  /**
   * Autosave's own state. It sits here rather than somewhere else in the rail because the design
   * treats these as one cluster — what is saved, what is missing, and what to do next — and they
   * move together into the bottom bar below `lg`.
   */
  save: Pick<Autosave, "status" | "savedAt" | "retry">;
  actions: FormActions;
  /** `column` in the rail on lg, `row` in the bottom bar below it. */
  orientation?: "column" | "row";
  /**
   * Opens and closes the preview sheet. Only the bottom bar is given one: on lg the preview is a
   * column that is always there, so there is nothing to toggle.
   */
  preview?: { open: boolean; onToggle: () => void };
}

/**
 * The form's footer: what is saved, what is missing, and the way out.
 *
 * On a draft, Finalize stays disabled while anything required is missing, and the count next to it
 * is a button — clicking it, or the disabled Finalize, takes the user to the first thing standing
 * in the way. That is what replaces the old "validate on Next, report via toast" model.
 *
 * On a published bulletin the count is still shown when there is one, because a live bulletin
 * missing a required field is worth knowing about and the officer can fix it on the spot. What it
 * does **not** say is "ready to finalize": there is nothing to finalize.
 */
export const FormActionBar: React.FC<FormActionBarProps> = ({
  requiredLeft,
  onJumpToFirstIssue,
  save,
  actions,
  orientation = "column",
  preview,
}) => {
  const { t } = useTranslation("bulletin/create-v2");
  const theme = useTheme();
  // On a phone this button shares a fixed bar with the save indicator, and "Back to bulletin"
  // wraps onto two lines and squeezes the indicator into an ellipsis. The arrow already says
  // "back", so the phone drops the destination rather than the whole label.
  const phone = useMediaQuery(theme.breakpoints.down("sm"));
  const ready = requiredLeft === 0;
  const column = orientation === "column";

  /**
   * What is missing, as a button: clicking it — or the disabled Finalize — goes to the first thing
   * standing in the way.
   */
  const count = (
    <Typography
      component="button"
      type="button"
      variant="caption"
      onClick={onJumpToFirstIssue}
      sx={{
        border: 0,
        background: "none",
        p: 0,
        font: "inherit",
        color: "primary.main",
        cursor: "pointer",
        // Centred in the rail, and on a phone, where it gets a line of its own. In the bar it
        // reads left-to-right out of the save indicator it sits beside.
        textAlign: column || phone ? "center" : "left",
        textDecoration: "underline",
      }}
    >
      {t("actions.required_left", { count: requiredLeft })}
    </Typography>
  );

  /** Where the bulletin stands: what is missing, or that nothing is. */
  const status =
    actions.kind === "published" ? (
      ready ? null : (
        count
      )
    ) : ready ? (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: column || phone ? "center" : "left" }}
      >
        {t("actions.ready")}
      </Typography>
    ) : (
      count
    );

  /** The one action that moves the bulletin on: Finalize, or on a live one, the way out. */
  const primaryAction =
    actions.kind === "published" ? (
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBack />}
        onClick={actions.onBack}
        fullWidth={column}
      >
        {t(phone ? "actions.back" : "actions.back_to_bulletin")}
      </Button>
    ) : (
      /*
        A disabled MUI button has `pointer-events: none`, so the click lands on this wrapper
        instead — which is how the disabled Finalize can still take the user to the first issue.
      */
      <Box
        onClick={ready ? undefined : onJumpToFirstIssue}
        sx={{ display: "flex", cursor: ready ? "default" : "pointer" }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={
            actions.busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AddTask />
            )
          }
          disabled={!ready || actions.busy}
          onClick={actions.onFinalize}
          fullWidth={column}
        >
          {t("actions.finalize")}
        </Button>
      </Box>
    );

  /**
   * Discard: labelled in the rail, an icon in the bar, where it also moves to the far end from
   * Finalize. Deleting the draft and publishing it are the two irreversible things on this page,
   * and in the bar they were a thumb-width apart.
   *
   * It stays primary rather than red — buttons on this page are primary, and what actually guards
   * the deletion is the dialog behind it.
   */
  const discard =
    actions.kind !== "draft" ? null : column ? (
      <Button
        variant="outlined"
        color="primary"
        disabled={actions.busy}
        onClick={actions.onDiscard}
        fullWidth
      >
        {t("actions.discard")}
      </Button>
    ) : (
      <Tooltip title={t("actions.discard")}>
        {/* A disabled IconButton fires no events, so the tooltip needs an element that does. */}
        <span>
          <IconButton
            color="primary"
            disabled={actions.busy}
            onClick={actions.onDiscard}
            aria-label={t("actions.discard")}
          >
            <DeleteOutline />
          </IconButton>
        </span>
      </Tooltip>
    );

  /**
   * The preview toggle belongs next to Finalize rather than at the end of the form column: below
   * lg the preview is a sheet over the page, so showing and hiding it is a bar action like the
   * rest, and the same control has to close what it opened.
   */
  // Open, it says where it goes rather than what it closes — the preview covers the form, so
  // leaving it is "Edit", the same word the sheet's own way back uses.
  const previewLabel = preview?.open
    ? t("actions.edit")
    : t("actions.show_preview");
  const previewIcon = preview?.open ? <EditOutlined /> : <VisibilityOutlined />;
  const previewToggle = !preview ? null : phone ? (
    <Tooltip title={previewLabel}>
      <IconButton
        color="primary"
        onClick={preview.onToggle}
        aria-label={previewLabel}
        sx={{ border: 1, borderColor: "primary.main", borderRadius: 1 }}
      >
        {previewIcon}
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      variant="outlined"
      color="primary"
      startIcon={previewIcon}
      onClick={preview.onToggle}
      sx={{ whiteSpace: "nowrap" }}
    >
      {previewLabel}
    </Button>
  );

  const indicator = (
    <SaveIndicator
      status={save.status}
      savedAt={save.savedAt}
      onRetry={save.retry}
      compact={phone && !column}
    />
  );

  /*
   * On a phone the bar is **two rows**, not one. Everything on one row left the count wrapping over
   * four lines, "Discard draft" over two, and the save indicator down to "Change…" — three
   * separate things squeezing each other for the same 570px. The count is the one that reads as a
   * sentence, so it gets the full width; the rest keep a row, which they fit as icons plus the one
   * label that matters.
   */
  if (phone && !column) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {status}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {discard}
          {indicator}
          <Box sx={{ flex: 1 }} />
          {previewToggle}
          {primaryAction}
        </Box>
      </Box>
    );
  }

  /*
   * The bar reads in two groups: what the draft is doing on the left — what is saved, what is
   * missing, and throwing it away — and what to do next on the right.
   */
  if (!column) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {discard}
        {indicator}
        {status}
        <Box sx={{ flex: 1 }} />
        {previewToggle}
        {primaryAction}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1,
      }}
    >
      {indicator}
      {status}
      {primaryAction}
      {discard}
    </Box>
  );
};
